import { LoadingSkeleton } from '../../../../common/components';
import GerencialesBarTableSection from '../GerencialesBarTableSection';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';
import { formatCurrency } from '../../../../utils/formatUtils';

/**
 * Renderiza el informe de saldos de deuda promedio.
 * @param {Object} props - Props del componente.
 * @param {boolean} props.loading - Estado de carga.
 * @param {Object|null} props.results - Resultados del reporte.
 * @param {Object|null} props.header - Header con metadata.
 * @returns {JSX.Element|null} Informe de saldos de deuda.
 */
const DebtBalanceReport = ({ loading, results, header }) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!results) {
    return null;
  }

  const totalContracts = header?.total_contracts ?? 0;
  const totalAverageDebt = header?.average_debt ?? 0;

  /**
   * Construye filas y data para grafico agregando el total.
   * @param {Array<Object>} rows - Filas base del backend.
   * @returns {{tableRows: Array<Object>, chartRows: Array<Object>}} Data formateada.
   */
  const buildSectionData = (rows) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const mergedRows = [
      ...safeRows,
      { label: 'Todos', count: totalContracts, average_debt: totalAverageDebt },
    ];

    const tableRows = mergedRows.map((row) => ({
      label: row.label,
      count: row.count,
      average_debt_label: formatCurrency(row.average_debt ?? 0),
      is_total: row.label === 'Todos',
    }));

    const chartRows = mergedRows.map((row, index) => ({
      name: row.label,
      value: Number(row.average_debt ?? 0),
      fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
    }));

    return { tableRows, chartRows };
  };

  const possessionData = buildSectionData(results.possession);
  const clientTypeData = buildSectionData(results.client_type);

  return (
    <>
      <GerencialesBarTableSection
        title="Deuda total a cancelar promedio por tipo de posesión"
        columns={[
          { key: 'label', label: 'Tipo posesión', align: 'left' },
          { key: 'count', label: 'Cant.', align: 'center' },
          { key: 'average_debt_label', label: 'Deuda total prom.', align: 'center' },
        ]}
        rows={possessionData.tableRows}
        chartData={possessionData.chartRows}
        chartId="gerenciales-debt-balance-possession"
        xAxisLabel="Deuda total promedio $"
      />
      <GerencialesBarTableSection
        title="Deuda total a cancelar promedio por tipo de cliente"
        columns={[
          { key: 'label', label: 'Tipo cliente', align: 'left' },
          { key: 'count', label: 'Cant.', align: 'center' },
          { key: 'average_debt_label', label: 'Deuda total prom.', align: 'center' },
        ]}
        rows={clientTypeData.tableRows}
        chartData={clientTypeData.chartRows}
        chartId="gerenciales-debt-balance-client-type"
        xAxisLabel="Deuda total promedio $"
      />
    </>
  );
};

export default DebtBalanceReport;
